import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@localagent/ui/components/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@localagent/ui/components/tabs';
import { toast } from '@localagent/ui/components/sonner';
import { uuidV4 } from '@localagent/sdk';
import { useArchiveWorkspace, useWorkspaceProjection } from '../hooks/useWorkspaces';
import { useAttachWorkspaceToChat } from '../hooks/useAttachWorkspaceToChat';
import {
  launchedWorkspaceIdsAtom,
  launchWorkspaceAction,
  dismissWorkspaceAction,
} from '../stores/workspace/atoms';
import { WorkspaceFilesPanel } from '../components/workspaces/WorkspaceFilesPanel';
import { WorkspaceIndexerStatus } from '../components/workspaces/WorkspaceIndexerStatus';
import { WorkspaceCloudDetailForProjection } from '../components/cloud-filesystem/ActiveWorkspaceCloudDetail';
import { WorkspaceStatePanelContent } from '../components/workspaces/WorkspaceStatePanel';
import { workspaceArchiveFailureMessage } from './workspaceArchiveFailure';
import { WorkspaceContextTab } from '../components/workspaces/WorkspaceContextTab';
import { WorkspaceTeamsList } from '../components/workspaces/WorkspaceTeamsList';
import { useWorkspaceTeams } from '../hooks/useWorkManagement';
import { useExperienceMode } from '../hooks/useExperienceMode';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { WorkspaceOverviewSummary } from '../components/workspaces/WorkspaceOverviewSummary';
import { WorkspaceDetailHeader } from '../components/workspaces/WorkspaceDetailHeader';
import { PAGE_TABS_LIST_CLASS, PAGE_TABS_TRIGGER_CLASS } from '../components/layout/PageTabs';
import { WORKSTATION_CARD_ID, WORKSTATION_FOCUS } from '../components/workspaces/workspaceLinks';

const WORKSPACE_DETAIL_TABS = ['overview', 'context', 'files'];

// Viewing is side-effect-free; launch, dismiss and archive are explicit acts (app-shell ADR-003).
export function WorkspaceDetailView() {
  useCatalogSubscription('context');
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const {
    projection: workspace,
    deviceId,
    isLoading,
    error,
    refreshRequested,
    revisionGap,
  } = useWorkspaceProjection(workspaceId ?? null);

  const launchedIds = useAtomValue(launchedWorkspaceIdsAtom);
  const launchWorkspace = useSetAtom(launchWorkspaceAction);
  const dismissWorkspace = useSetAtom(dismissWorkspaceAction);
  const archiveWorkspace = useArchiveWorkspace();
  const { data: workspaceTeams, error: workspaceTeamsError } = useWorkspaceTeams(workspaceId ?? '');
  const { attach, attaching } = useAttachWorkspaceToChat();
  const { mode: experienceMode } = useExperienceMode();
  const showAdvancedChrome = experienceMode === 'advanced';
  const showNormalStatus = experienceMode === 'normal';

  useEffect(() => {
    if (workspace?.lifecycle === 'archived') {
      dismissWorkspace(workspace.workspace_id);
    }
  }, [dismissWorkspace, workspace]);

  const [searchParams, setSearchParams] = useSearchParams();
  const liveSearchParams = useRef(searchParams);
  liveSearchParams.current = searchParams;

  // URL state has exactly one writer, and it is never an effect (app-shell ADR-006).
  const writeUrl = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const current = liveSearchParams.current;
      const next = new URLSearchParams(current);
      mutate(next);
      if (next.toString() === current.toString()) return;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && WORKSPACE_DETAIL_TABS.includes(requestedTab)
      ? requestedTab
      : searchParams.has('path')
        ? 'files'
        : 'overview';

  const focusWorkstation =
    activeTab === 'overview' && searchParams.get('focus') === WORKSTATION_FOCUS && Boolean(workspace);
  useEffect(() => {
    if (!focusWorkstation) return;
    document.getElementById(WORKSTATION_CARD_ID)?.scrollIntoView({ block: 'start' });
  }, [focusWorkstation]);

  const handleTabChange = useCallback(
    (tab: string) => {
      writeUrl((next) => {
        if (tab === 'overview') next.delete('tab');
        else next.set('tab', tab);
      });
    },
    [writeUrl]
  );

  if (isLoading) {
    return (
      <LoadingState label="Loading workspace..." className="h-full" />
    );
  }

  // Explicit error state: unknown id or load failure gets a loud
  // block with a way back — no silent redirect, no empty render.
  if (error || !workspace) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="workspace-detail-error">
        <div className="text-center">
          <p className="text-destructive">Failed to load workspace</p>
          {error && <p className="mt-2 text-body text-muted-foreground">{error.message}</p>}
          <Button
            variant="outline"
            className="mt-4"
            data-testid="workspace-detail-back"
            onClick={() => void navigate('/workspaces')}
          >
            Back to Workspaces
          </Button>
        </div>
      </div>
    );
  }

  const isLaunched = launchedIds.includes(workspace.workspace_id);
  const mode = workspace.assignment.routing_class;
  const workspacePath = workspace.binding?.folder_path ?? '';

  return (
    <div className="flex h-full flex-col" data-testid="workspace-detail">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex min-h-0 flex-1 flex-col"
      >
        <WorkspaceDetailHeader
          workspace={workspace}
          showAdvancedChrome={showAdvancedChrome}
          isLaunched={isLaunched}
          attaching={attaching}
          archivePending={archiveWorkspace.isPending}
          teams={workspaceTeams}
          teamsError={workspaceTeamsError}
          onBack={() => void navigate('/workspaces')}
          onOpenInChat={() => attach(workspace.workspace_id)}
          onLaunch={() => launchWorkspace(workspace.workspace_id)}
          onDismiss={() => dismissWorkspace(workspace.workspace_id)}
          onArchive={({ onSuccess, onError }) =>
            archiveWorkspace.mutate(
              { workspaceId: workspace.workspace_id, idempotencyKey: uuidV4() },
              {
                onSuccess: () => {
                  onSuccess();
                  handleTabChange('overview');
                  toast.success(`Archive started for workspace "${workspace.name}"`);
                },
                onError: (archiveError) => {
                  const message = workspaceArchiveFailureMessage(archiveError);
                  onError(message);
                  toast.error(message);
                },
              }
            )
          }
          tabs={
            <TabsList aria-label="Workspace" className={PAGE_TABS_LIST_CLASS}>
              <TabsTrigger value="overview" data-testid="workspace-detail-tab-overview" className={PAGE_TABS_TRIGGER_CLASS}>
                Overview
              </TabsTrigger>
              <TabsTrigger value="context" data-testid="workspace-detail-tab-context" className={PAGE_TABS_TRIGGER_CLASS}>
                Context
              </TabsTrigger>
              <TabsTrigger value="files" data-testid="workspace-detail-tab-files" className={PAGE_TABS_TRIGGER_CLASS}>
                Files
              </TabsTrigger>
            </TabsList>
          }
        />
        <TabsContent value="files" className="min-h-0 flex-1 pt-2">
          <WorkspaceFilesPanel
            workspaceId={workspace.workspace_id}
            workspacePath={workspacePath}
            mode={mode}
          />
        </TabsContent>
        <TabsContent value="overview" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {showNormalStatus ? <WorkspaceOverviewSummary projection={workspace} /> : null}
            {workspaceTeamsError ? (
              <p className="text-body text-destructive" role="alert" data-testid="workspace-teams-error">
                Could not load the teams using this workspace: {workspaceTeamsError.message}
              </p>
            ) : workspaceTeams ? (
              <WorkspaceTeamsList teams={workspaceTeams} />
            ) : null}
            {showAdvancedChrome ? (
              <WorkspaceStatePanelContent
                projection={workspace}
                deviceId={deviceId}
                isLoading={isLoading}
                error={error}
                refreshRequested={refreshRequested}
                revisionGap={revisionGap}
              />
            ) : null}
            {showAdvancedChrome ? (
              <WorkspaceIndexerStatus projection={workspace} workspacePath={workspacePath} />
            ) : null}
            {showAdvancedChrome ? (
              <WorkspaceCloudDetailForProjection projection={workspace} />
            ) : null}
          </div>
        </TabsContent>
        <TabsContent value="context" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <WorkspaceContextTab
            workspaceId={workspace.workspace_id}
            showAdvanced={showAdvancedChrome}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
